import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, Download, Share2, Send, Ticket, Loader2, CheckCircle2, Clock, Star } from "lucide-react";
import { sr } from "../../lib/sr";

export default function OfferLanding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [claimed, setClaimed] = useState(null);
  const [sid] = useState(() => Math.random().toString(36).slice(2));
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    sr.offerStudio.landing(token).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Offer not found"));
  }, [token]);

  if (err) return <Shell><div style={{ textAlign: "center", padding: 40 }}><h2>{err}</h2></div></Shell>;
  if (!data) return <Shell><div style={{ textAlign: "center", padding: 60 }}><Loader2 className="sr-spin" size={28} /></div></Shell>;

  const o = data.offer;
  const c = o.colors || { bg: "#111827", accent: "#D4AF37", fg: "#FFFFFF" };
  const providers = data.wallet_providers || [];
  const demoAny = providers.some((p) => p.demo);

  const claim = async () => {
    setBusy("claim");
    try { const r = await sr.offerStudio.claim(token, { session_id: sid }); setClaimed(r.code); toast.success("Coupon unlocked!"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not claim"); } finally { setBusy(""); }
  };
  const apple = () => { window.location.href = sr.offerStudio.applePassUrl(token) + `?s=${sid}`; };
  const google = async () => {
    setBusy("google");
    try { const r = await sr.offerStudio.googlePass(token, { session_id: sid }); window.open(r.save_url, "_blank"); if (r.demo) toast.message("Demo Google Wallet link (real link once credentials are added)"); }
    catch (e) { toast.error("Could not create Google pass"); } finally { setBusy(""); }
  };
  const download = () => { window.open(sr.offerStudio.downloadUrl(token) + `?s=${sid}`, "_blank"); };
  const share = async () => { try { const r = await sr.offerStudio.share(token); await navigator.clipboard.writeText(r.share_url); toast.success("Share link copied"); } catch { toast.error("Could not share"); } };
  const send = async () => {
    if (!to.trim()) return toast.error("Enter a phone or email");
    setBusy("send");
    try { await sr.offerStudio.send(token, { to: to.trim(), session_id: sid }); toast.success("Sent! Check your phone/email."); setTo(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(""); }
  };

  const btn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "14px 16px", borderRadius: 12, fontWeight: 700, fontSize: 15, border: "none", cursor: "pointer" };

  return (
    <Shell>
      <div data-testid="offer-landing" style={{ maxWidth: 460, margin: "0 auto", padding: "24px 16px 60px" }}>
        {o.expired && <div style={{ background: "#FDECEC", color: "#B42318", padding: 10, borderRadius: 10, textAlign: "center", marginBottom: 12, fontWeight: 700 }} data-testid="offer-expired">This offer has expired.</div>}
        {!o.expired && o.not_started && <div style={{ background: "#FEF6E7", color: "#9A7412", padding: 10, borderRadius: 10, textAlign: "center", marginBottom: 12, fontWeight: 700 }} data-testid="offer-not-started">This offer starts {o.starts_at ? new Date(o.starts_at).toLocaleDateString() : "soon"}.</div>}
        {/* coupon card */}
        <div style={{ background: c.bg, borderRadius: 18, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,.25)" }}>
          <div style={{ height: 6, background: c.accent }} />
          <div style={{ padding: 24 }}>
            <div style={{ color: c.accent, fontWeight: 800, letterSpacing: 1, fontSize: 13 }}>{(o.org_name || "").toUpperCase()}</div>
            <h1 style={{ color: c.fg, fontSize: 30, lineHeight: 1.15, margin: "10px 0" }} data-testid="landing-headline">{o.headline}</h1>
            <p style={{ color: c.fg, opacity: 0.85, fontSize: 15 }}>{o.description ?? o.offer_copy}</p>
            {o.minimum_purchase != null && <div style={{ color: c.fg, opacity: 0.7, fontSize: 12, marginTop: 6 }} data-testid="landing-min-purchase">Minimum purchase: ${o.minimum_purchase}</div>}
            {data.business?.rating != null && (
              <div data-testid="landing-trust-badge" style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "rgba(255,255,255,0.10)", border: `1px solid ${c.accent}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ color: c.accent, fontWeight: 800, fontSize: 18 }} data-testid="landing-rating">{data.business.rating}</span>
                  <div style={{ display: "flex", gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} style={{ color: c.accent, fill: i <= Math.round(data.business.rating) ? c.accent : "none" }} />)}
                  </div>
                  <span style={{ color: c.fg, opacity: 0.7, fontSize: 12 }}>on Google{data.business.rating_count ? ` · ${data.business.rating_count} reviews` : ""}</span>
                </div>
                {data.business.review?.text && <p style={{ color: c.fg, opacity: 0.9, fontSize: 13, fontStyle: "italic", marginTop: 8 }} data-testid="landing-review">“{String(data.business.review.text).slice(0, 150)}{String(data.business.review.text).length > 150 ? "…" : ""}”</p>}
              </div>
            )}
            {o.assets?.coupon_png && <img src={o.assets.coupon_png} alt="coupon" style={{ width: "100%", borderRadius: 12, marginTop: 16 }} />}
            {o.expires_at && <div style={{ color: c.accent, fontSize: 13, marginTop: 14, display: "flex", alignItems: "center", gap: 6 }}><Clock size={13} /> Expires {new Date(o.expires_at).toLocaleDateString()}</div>}
          </div>
        </div>

        {/* claim / code */}
        <div style={{ marginTop: 18 }}>
          {!claimed ? (
            <button style={{ ...btn, background: c.accent, color: c.bg }} onClick={claim} disabled={busy === "claim" || o.expired || o.not_started} data-testid="landing-claim">
              {busy === "claim" ? <Loader2 className="sr-spin" size={18} /> : <><Ticket size={18} /> {o.cta || "Get the coupon"}</>}
            </button>
          ) : (
            <div style={{ background: "#fff", border: `2px dashed ${c.accent}`, borderRadius: 12, padding: 16, textAlign: "center" }} data-testid="landing-code">
              <div style={{ fontSize: 12, color: "var(--muted)" }}>YOUR CODE</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "var(--navy)", letterSpacing: 1 }}>{claimed}</div>
              <div style={{ fontSize: 12, color: "#067647", marginTop: 4 }}><CheckCircle2 size={12} /> Show this at checkout</div>
            </div>
          )}
        </div>

        {/* wallet + save */}
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <button style={{ ...btn, background: "#000", color: "#fff" }} onClick={apple} disabled={o.expired} data-testid="add-apple-wallet"><Wallet size={18} /> Add to Apple Wallet</button>
          <button style={{ ...btn, background: "#1a73e8", color: "#fff" }} onClick={google} disabled={busy === "google" || o.expired} data-testid="add-google-wallet">{busy === "google" ? <Loader2 className="sr-spin" size={18} /> : <><Wallet size={18} /> Add to Google Wallet</>}</button>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...btn, background: "#fff", color: "var(--navy)", border: "1px solid var(--border)" }} onClick={download} data-testid="download-coupon"><Download size={16} /> Save</button>
            <button style={{ ...btn, background: "#fff", color: "var(--navy)", border: "1px solid var(--border)" }} onClick={share} data-testid="share-offer"><Share2 size={16} /> Share</button>
          </div>
        </div>

        {/* send to phone / email */}
        <div style={{ marginTop: 16, background: "#fff", borderRadius: 12, padding: 14, border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>Send it to your phone or email</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input className="sr-input" placeholder="phone or email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="send-input" style={{ flex: 1 }} />
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={send} disabled={busy === "send"} data-testid="send-btn">{busy === "send" ? <Loader2 className="sr-spin" size={14} /> : <Send size={14} />}</button>
          </div>
        </div>

        {/* fine print + rules */}
        {(o.redemption_rules || []).length > 0 && (
          <ul style={{ marginTop: 16, paddingLeft: 18, color: "var(--muted)", fontSize: 12 }}>
            {o.redemption_rules.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
        {o.fine_print && <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 8 }}>{o.fine_print}</p>}
        {o.terms && <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 6, whiteSpace: "pre-wrap" }} data-testid="landing-terms">{o.terms}</p>}
        {demoAny && <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 8 }}>Wallet passes are in demo mode — fully installable once wallet credentials are added.</p>}
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="sr-root" style={{ minHeight: "100vh", background: "linear-gradient(180deg,#0b1220 0%,#1a2338 100%)" }}>
    {children}
  </div>
);
